import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Col, Row, message } from 'antd';
import { RetweetOutlined } from '@ant-design/icons';
import { Formik } from 'formik';
import { Form, Select, Input } from 'formik-antd';
import moment from 'moment';
import { useLocation } from 'react-router-dom';
import * as Yup from 'yup';
import useAuth from 'hooks/useAuth';
import { Box, FormInput, CustomInput, Button } from 'atoms';
import { DatePickers } from 'utils/commonStyled';
import {
	VEHICLE_REPLACEMENT_STATUS_IDS,
	DRIVER_REPLACEMENT_STATUS_IDS,
	DRIVER_DROPDOWN_EDITABLE_STATUS_IDS,
	DRIVER_ASSIGN_WHEN_UNASSIGNED_STATUS_IDS,
} from 'utils/tripStatus';
import { ACL, EditModeBtn } from 'components/common';
import ReplaceVehicleModal from 'pagesv2/vehiclesv2/edit-vehiclev2/ReplaceVehicleModal';
import VehicleSelectionFromAnotherVendorModal from 'pagesv2/vehiclesv2/edit-vehiclev2/VehicleSelectionFromAnotherVendorModal';
import ReplaceDriverModal from 'pagesv2/vehiclesv2/edit-vehiclev2/ReplaceDriverModal';
import UploadWithPreview from '../common/UploadWithPreview';

const FormContainer = styled(Box)`
	max-width: 1200px;
	margin: 32px auto;
	background: #fff;
	border-radius: 8px;
	box-shadow: 0 2px 16px rgba(0, 0, 0, 0.07);
	padding: 40px 60px 32px 60px;
`;
const FormButtonContainer = styled.div`
	display: flex;
	justify-content: flex-end;
	gap: 12px;
	margin-top: 18px;
`;
const FormPrimaryButton = styled(Button)`
	background: #18405d;
	color: #fff;
	border: 1px solid #18405d;
	border-radius: 4px;
	padding: 7px 24px;
	font-size: 15px;
	font-weight: 500;

	&:not(:disabled) {
		background: #18405d !important;
		border-color: #18405d !important;
		color: #fff !important;
		opacity: 1 !important;
		cursor: pointer;
	}

	&:disabled {
		background: #cbd5e1 !important;
		border-color: #cbd5e1 !important;
		color: #fff !important;
		opacity: 1 !important;
		cursor: not-allowed;
	}
`;
const FormCancelButton = styled(Button)`
	background: #fff;
	color: #18405d;
	border: 1px solid #18405d;
	border-radius: 4px;
	padding: 7px 24px;
	font-size: 15px;
	font-weight: 500;
`;
const CompactRow = styled(Row)`
	margin-bottom: 10px !important;
`;
const CompactCol = styled(Col)`
	margin-bottom: 0 !important;
`;
const LocationFieldRow = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
`;
const PincodeInput = styled.div`
	width: 120px;
	margin-bottom: 0 !important;
`;
const CityStateInput = styled.div`
	flex: 1;
	margin-bottom: 0 !important;
`;
const ReplaceLinkRow = styled.div`
	display: flex;
	justify-content: flex-end;
	margin: 4px 0 8px;
`;
const ReplaceLink = styled.button`
	border: none;
	background: none;
	color: #1890ff;
	font-size: 13px;
	font-weight: 500;
	cursor: pointer;
	display: inline-flex;
	align-items: center;
	gap: 6px;
	padding: 0;

	&:hover {
		color: #40a9ff;
		text-decoration: underline;
	}

	&:disabled {
		color: #94a3b8;
		cursor: not-allowed;
		text-decoration: none;
	}
`;
// eslint-disable-next-line sonarjs/cognitive-complexity
export default function IntercityTripForm({
	isEditMode = false,
	formData,
	cities,
	projectData = [],
	routeData = [],
	vendorsData = [],
	vehicleTypes = [],
	vehicleTypeIdMap = {},
	vehicleNumbers = [],
	driverData = [],
	isV2UI = false, // Flag to show v2-only fields
	sourceCity,
	destCity,
	uploading,
	uploadError,
	vendorUserType = [],
	setPendingValues,
	formRef,
	onCityChange,
	onProjectChange,
	onRouteChange,
	onVendorTypeChange,
	onVendorChange,
	onVehicleTypeChange,
	onVehicleNumberChange,
	onDriverChange,
	clientAllowsCrossVendor = false,
	onCrossVendorSelect,
	onPincodeChange,
	onProofUpload,
	onSubmit,
	navigate,
	isvendorBilling,
}) {
	const location = useLocation();
	const { permissions, v2_permissions } = useAuth();
	const [isFormValid, setIsFormValid] = React.useState(false);
	const validationTimeoutRef = React.useRef(null);
	const prevValuesRef = React.useRef(null);
	const initialSourceRef = React.useRef(location.state?.source);

	const [isFormEditable, setIsFormEditable] = React.useState(false);
	const [replaceVehicleVisible, setReplaceVehicleVisible] = React.useState(false);
	const [replaceDriverVisible, setReplaceDriverVisible] = React.useState(false);
	const [crossVendorVisible, setCrossVendorVisible] = React.useState(false);
	const showReplaceActions = isEditMode && isV2UI && !formData?.isEnded;
	const currentStatusId = Number(formData?.trip_status_id);
	const hasNoDriverAssigned = !formData?.driver_id;
	const updatePermissions = isV2UI ? v2_permissions?.update : permissions?.update;
	const hasReplaceVehiclePermission =
		updatePermissions?.includes('trip_replace_vehicle') || updatePermissions?.includes('all');
	const hasReplaceDriverPermission =
		updatePermissions?.includes('trip_replace_driver') || updatePermissions?.includes('all');
	const canReplaceVehicle =
		showReplaceActions &&
		hasReplaceVehiclePermission &&
		VEHICLE_REPLACEMENT_STATUS_IDS.includes(currentStatusId);
	const canReplaceDriver =
		showReplaceActions &&
		hasReplaceDriverPermission &&
		!hasNoDriverAssigned &&
		DRIVER_REPLACEMENT_STATUS_IDS.includes(currentStatusId);
	const isDriverStatusEditable = DRIVER_DROPDOWN_EDITABLE_STATUS_IDS.includes(currentStatusId);
	const canSelectCrossVendorVehicle = isV2UI && !isEditMode && !!clientAllowsCrossVendor;
	const canAssignDriverForLaterStatus =
		isV2UI &&
		isEditMode &&
		isFormEditable &&
		hasNoDriverAssigned &&
		DRIVER_ASSIGN_WHEN_UNASSIGNED_STATUS_IDS.includes(currentStatusId);
	// Check if coming from adhoc-request or create-lr with pre-filled data
	const ADHOC_REQUEST = 'adhoc-request';
	const CREATE_LR = 'create-lr';
	const currentSource = location.state?.source;
	const hasPrefilledData = currentSource === ADHOC_REQUEST || currentSource === CREATE_LR;
	const isAdhocPrefilled =
		initialSourceRef.current === ADHOC_REQUEST || initialSourceRef.current === CREATE_LR;
	const isStrictLocked = (fieldValue) => isV2UI && isAdhocPrefilled && !!fieldValue;

	// Helper function to restore form data
	// eslint-disable-next-line sonarjs/cognitive-complexity
	const restoreFormData = React.useCallback(
		(storedFormData) => {
			if (!formRef.current) return;

			Object.keys(storedFormData).forEach((key) => {
				const val = storedFormData[key];
				if (val === undefined || val === null) return;

				if (key === 'reporting_date' || key === 'start_date') {
					if (!val) return;
					const momentDate = moment(val);
					if (momentDate.isValid()) {
						formRef.current.setFieldValue(key, momentDate.toDate());
					}
				} else {
					formRef.current.setFieldValue(key, val);
				}
			});
		},
		[formRef]
	);
	// Check for stored form data from create-lr or adhoc-request
	React.useEffect(() => {
		if (
			location.state?.formData &&
			(currentSource === CREATE_LR || currentSource === ADHOC_REQUEST)
		) {
			restoreFormData(location.state.formData);
			// Clear the state to prevent re-filling on refresh
			navigate(location.pathname, { replace: true });
		}
	}, [currentSource, location.state, navigate, location.pathname, restoreFormData]);

	const initialValues = React.useMemo(
		// eslint-disable-next-line sonarjs/cognitive-complexity
		() => ({
			trip_type: formData?.trip_type || 'adhoc',
			city: formData?.city || '',
			project_id: formData?.project_id || '', // use ID
			route_id: formData?.route_id || '',
			vendor_id: formData?.vendor_id || '', // use ID
			vendor_type: formData?.vendor_type || '',
			vehicle_number: formData?.vehicle_number || '',
			reporting_date: formData?.reporting_date || undefined,
			start_date: formData?.start_date || undefined,
			start_location_pincode: formData?.start_location_pincode || '',
			end_location_pincode: formData?.end_location_pincode || '',
			vehicle_type: formData?.vehicle_type || '',
			driver_id: formData?.driver_id ? formData.driver_id : undefined,
			driver_name: formData?.driver_name || '',
			driver_number: formData?.driver_number || '',
			requirement_proof: formData?.requirement_proof || [],
			client_rate: formData?.client_rate || '',
			vendor_rate: formData?.vendor_rate || '',
			adhoc_request_id: formData?.adhoc_request_id || '',
			remarks: formData?.remarks || '',
			start_km: formData?.start_km ?? '',
		}),
		[formData]
	);

	// Validation schema for IntercityTripForm (mirroring Intracity)
	const validationSchema = Yup.object({
		trip_type: Yup.string().required().label('Trip Type'),
		city: Yup.string().required().label('City'),
		project_id: Yup.mixed()
			.required('Project is required')
			.test(
				'is-valid',
				'Project is required',
				(value) => value !== undefined && value !== null && value !== ''
			)
			.label('Project'),
		route_id: isV2UI
			? Yup.mixed()
					.required('Route is required')
					.test(
						'is-valid',
						'Route is required',
						(value) => value !== undefined && value !== null && value !== ''
					)
					.label('Route')
			: Yup.mixed().optional(),
		vendor_id: Yup.mixed()
			.required('Vendor is required')
			.test(
				'is-valid',
				'Vendor is required',
				(value) => value !== undefined && value !== null && value !== ''
			)
			.label('Vendor'),
		vendor_type: Yup.string().required().label('Vendor Type'),
		vehicle_number: Yup.string()
			.min(8)
			.max(12)
			.matches(
				/^(?:[0-9]{2}BH[0-9]{4}[A-Z]{1,2}|[A-Z]{2}[0-9]{1,2}[A-Z]{1,3}[0-9]{1,4}|[A-Z]{2}[0-9]{4,8})$/,
				'Enter Valid Vehicle Number'
			)
			.required()
			.label('Vehicle Number'),
		reporting_date: Yup.mixed()
			.required('Reporting Date is required')
			.test(
				'is-valid-date',
				'Invalid reporting date',
				(value) => value && (value instanceof Date || moment(value).isValid())
			)
			.label('Reporting Date'),
		start_date: Yup.mixed()
			.required('Start Date is required')
			.test(
				'is-valid-date',
				'Invalid start date',
				(value) => value && (value instanceof Date || moment(value).isValid())
			)
			.test(
				'start-after-reporting',
				'Start Date cannot be before Reporting Date',
				(value, context) => {
					const parent = context?.parent || {};
					const { reporting_date } = parent;
					if (!value || !reporting_date) return true;
					const startDate = moment(value);
					const reportingDate = moment(reporting_date);
					return startDate.isSameOrAfter(reportingDate);
				}
			)
			.label('Start Date'),
		vehicle_type: Yup.string().required().label('Vehicle Type'),
		driver_id: isV2UI
			? Yup.mixed()
					.when('vehicle_number', {
						is: (vehicleNumber) => vehicleNumber && String(vehicleNumber).length > 0,
						then: (schema) =>
							schema
								.required('Driver is required')
								.test(
									'is-valid',
									'Driver is required',
									(value) => value !== undefined && value !== null && value !== ''
								),
						otherwise: (schema) => schema.optional(),
					})
					.label('Driver')
			: Yup.mixed().optional().label('Driver'),
		driver_name: isV2UI
			? Yup.string().optional().label('Driver Name')
			: Yup.string().required().label('Driver Name'),
		driver_number: isV2UI
			? Yup.string().optional().label('Driver Number')
			: Yup.string()
					.required('Driver number is required')
					.matches(/^[6-9]/, 'Driver number must start with 6, 7, 8, or 9')
					.matches(
						/^[6-9][0-9]{9}$/,
						'Driver number must be exactly 10 digits starting with 6, 7, 8, or 9'
					)
					.test('max-length', 'Driver number cannot exceed 10 digits', (value) =>
						value ? value.length <= 10 : true
					)
					.label('Driver Number'),
		client_rate: Yup.mixed()
			.required('Client Rate is required')
			.test('is-number', 'Client rate must be a number', (value) => {
				if (!value && value !== 0) return false;
				const num = Number(value);
				return !Number.isNaN(num) && num > 0;
			})
			.test(
				'is-greater',
				'Client rate must be greater than vendor rate',
				(value, context) => {
					const parent = context?.parent || {};
					const { vendor_rate } = parent;
					if (!value || !vendor_rate) return false;
					return Number(value) > Number(vendor_rate);
				}
			)
			.label('Client Rate'),
		vendor_rate: Yup.mixed()
			.required('Vendor Rate is required')
			.test('is-number', 'Vendor rate must be a number', (value) => {
				if (!value && value !== 0) return false;
				const num = Number(value);
				return !Number.isNaN(num) && num > 0;
			})
			.label('Vendor Rate'),
		start_location_pincode: Yup.string()
			.required('Source pincode is required')
			.matches(/^\d{6}$/, 'Enter a valid 6 digit pincode')
			.label('Source Pincode'),
		end_location_pincode: Yup.string()
			.required('Destination pincode is required')
			.matches(/^\d{6}$/, 'Enter a valid 6 digit pincode')
			.test(
				'different-from-source',
				'Destination pincode must be different from source pincode',
				(value, context) => {
					const { start_location_pincode, trip_type } = context.parent || {};
					if (trip_type === 'adhoc') return true;
					if (!value || !start_location_pincode) return true;
					return value !== start_location_pincode;
				}
			)
			.label('Destination Pincode'),
		requirement_proof: Yup.array()
			.test(
				'has-files',
				'Proof of request is required',
				(value) => value && Array.isArray(value) && value.length > 0
			)
			.label('Proof of Request'),
		remarks: Yup.string().max(250, 'Remarks cannot exceed 250 characters').label('Remarks'),
		start_km: isEditMode
			? Yup.number()
					.typeError('Start KM must be a number')
					.required('Start KM is required')
					.min(0, 'Start KM cannot be negative')
					.label('Start KM')
			: Yup.mixed().optional(),
	});

	return (
		<FormContainer>
			<Formik
				initialValues={initialValues}
				validationSchema={validationSchema}
				onSubmit={onSubmit}
				enableReinitialize
				validateOnChange
				validateOnBlur
				innerRef={formRef}
			>
				{({
					values,
					errors,
					touched,
					setFieldValue,
					isSubmitting,
					setSubmitting,
					validateForm,
					// eslint-disable-next-line sonarjs/cognitive-complexity
				}) => {
					// Watch form values and validate automatically to update Next button state
					// Use refs to track values and debounce validation without hooks
					const valuesString = JSON.stringify(values);
					if (prevValuesRef.current !== valuesString) {
						prevValuesRef.current = valuesString;

						// Clear existing timeout
						if (validationTimeoutRef.current) {
							clearTimeout(validationTimeoutRef.current);
						}

						// Debounce validation to avoid excessive calls
						validationTimeoutRef.current = setTimeout(async () => {
							try {
								const validationErrors = await validateForm();
								const valid = Object.keys(validationErrors).length === 0;
								setIsFormValid(valid);
							} catch (error) {
								setIsFormValid(false);
							}
						}, 300); // 300ms debounce
					}

					return (
						<>
							<ACL action="update" module="trip">
								{isEditMode && !formData?.isEnded && (
									<Row gutter={16} justify="space-between">
										<div></div>
										<EditModeBtn
											label={isFormEditable ? 'Cancel Edit' : 'Edit'}
											handleEdit={() => setIsFormEditable((prev) => !prev)}
										/>
									</Row>
								)}
							</ACL>
							<Form className="" disabled={isEditMode && !isFormEditable}>
								{/* Replicate screenshot field order and grouping */}
								<CompactRow gutter={16}>
									<CompactCol span={12}>
										<FormInput name="trip_type" label="Trip Type *">
											<Select
												name="trip_type"
												options={[{ value: 'adhoc', label: 'Adhoc' }]}
												disabled
											/>
										</FormInput>
									</CompactCol>
									<CompactCol span={12}>
										<FormInput name="city" label="City *">
											<Select
												key={`city-select-${isFormEditable}`} // Re-mount when edit toggles
												name="city"
												showSearch
												options={cities}
												placeholder="Select"
												onChange={(e) => {
													if (isV2UI && isStrictLocked(values.city)) {
														return;
													}
													onCityChange(e, setFieldValue);
												}}
												disabled={
													isEditMode ||
													(isV2UI && isStrictLocked(values.city)) ||
													(hasPrefilledData && formData?.city)
												}
											/>
										</FormInput>
									</CompactCol>
								</CompactRow>
								<CompactRow gutter={16}>
									<CompactCol span={12}>
										<FormInput
											name="reporting_date"
											label="Reporting Date & Time *"
										>
											<DatePickers
												name="reporting_date"
												showTime
												format="DD/MM/YYYY h:mm A"
												disabledDate={(current) =>
													current && current > moment().endOf('day')
												}
												disabled={
													isEditMode ||
													(isV2UI &&
														isStrictLocked(values.reporting_date)) ||
													(hasPrefilledData && formData?.reporting_date)
												}
											/>
										</FormInput>
									</CompactCol>
									<CompactCol span={12}>
										<FormInput name="start_date" label="Start Date & Time *">
											<DatePickers
												name="start_date"
												showTime
												format="DD/MM/YYYY h:mm A"
												disabled={
													isEditMode ||
													(isV2UI && isStrictLocked(values.start_date)) ||
													(hasPrefilledData && formData?.start_date)
												}
											/>
										</FormInput>
									</CompactCol>
								</CompactRow>
								<CompactRow gutter={16}>
									<CompactCol span={12}>
										<FormInput
											name="start_location_pincode"
											label="Source Location *"
										>
											<LocationFieldRow>
												<PincodeInput>
													<CustomInput
														name="start_location_pincode"
														placeholder="Pincode"
														maxLength={6}
														onChange={(e) => {
															if (
																isV2UI &&
																isStrictLocked(
																	values.start_location_pincode
																)
															) {
																return;
															}
															setFieldValue(
																'start_location_pincode',
																e.target.value
															);
															onPincodeChange(
																e.target.value,
																'source',
																setFieldValue
															);
														}}
														disabled={
															isEditMode ||
															(isV2UI &&
																isStrictLocked(
																	values.start_location_pincode
																)) ||
															(hasPrefilledData &&
																formData?.start_location_pincode)
														}
													/>
												</PincodeInput>
												<CityStateInput>
													<CustomInput
														name="start_location_city_state"
														value={sourceCity}
														placeholder="City, State"
														disabled
													/>
												</CityStateInput>
											</LocationFieldRow>
										</FormInput>
									</CompactCol>
									<CompactCol span={12}>
										<FormInput
											name="end_location_pincode"
											label="Destination Location *"
										>
											<LocationFieldRow>
												<PincodeInput>
													<CustomInput
														name="end_location_pincode"
														placeholder="Pincode"
														maxLength={6}
														onChange={(e) => {
															if (
																isV2UI &&
																isStrictLocked(
																	values.end_location_pincode
																)
															) {
																return;
															}
															setFieldValue(
																'end_location_pincode',
																e.target.value
															);
															onPincodeChange(
																e.target.value,
																'destination',
																setFieldValue
															);
														}}
														disabled={
															isEditMode ||
															(isV2UI &&
																isStrictLocked(
																	values.end_location_pincode
																)) ||
															(hasPrefilledData &&
																formData?.end_location_pincode)
														}
													/>
												</PincodeInput>
												<CityStateInput>
													<CustomInput
														name="end_location_city_state"
														value={destCity}
														placeholder="City, State"
														disabled
													/>
												</CityStateInput>
											</LocationFieldRow>
										</FormInput>
									</CompactCol>
								</CompactRow>
								<CompactRow gutter={16}>
									<CompactCol span={12}>
										<FormInput name="vendor_type" label="Vendor Type *">
											<Select
												name="vendor_type"
												showSearch
												options={vendorUserType}
												placeholder="Select"
												onChange={(e) =>
													isV2UI && isStrictLocked(values.vendor_type)
														? null
														: onVendorTypeChange(e, setFieldValue)
												}
												disabled={
													isEditMode ||
													(isV2UI &&
														isStrictLocked(values.vendor_type)) ||
													(hasPrefilledData && formData?.vendor_type)
												}
											/>
										</FormInput>
									</CompactCol>
									<CompactCol span={12}>
										<FormInput name="project_id" label="Select Project *">
											<Select
												name="project_id"
												showSearch
												options={projectData} // projectData: [{ value: id, label: name }]
												placeholder="Select"
												filterOption={(input, option) =>
													option.label
														.toLowerCase()
														.indexOf(input.toLowerCase()) >= 0
												}
												onChange={(e) =>
													isV2UI && isStrictLocked(values.project_id)
														? null
														: onProjectChange(e, setFieldValue, values)
												}
												disabled={
													isEditMode ||
													(isV2UI && isStrictLocked(values.project_id)) ||
													(hasPrefilledData && formData?.project_id)
												}
											/>
										</FormInput>
									</CompactCol>
								</CompactRow>
								{isV2UI && (
									<CompactRow gutter={16}>
										<CompactCol span={12}>
											<FormInput name="route_id" label="Select Route *">
												<Select
													key={`route-select-${
														values.route_id || 'empty'
													}-${routeData.length}`}
													name="route_id"
													showSearch
													options={routeData}
													placeholder="Select"
													onChange={(e) => {
														if (isAdhocPrefilled && values.route_id) {
															return;
														}
														onRouteChange(e, setFieldValue, values);
													}}
													disabled={
														isEditMode ||
														(isAdhocPrefilled &&
															(formData?.route_id ||
																values.route_id)) ||
														!values.project_id
													}
													filterOption={(input, option) =>
														option.label
															.toLowerCase()
															.indexOf(input.toLowerCase()) >= 0
													}
												/>
											</FormInput>
										</CompactCol>
									</CompactRow>
								)}
								<CompactRow gutter={16}>
									<CompactCol span={12}>
										<FormInput name="vendor_id" label="Select Vendor *">
											<Select
												name="vendor_id"
												showSearch
												options={vendorsData} // vendorsData: [{ value: id, label: name }]
												placeholder="Select"
												disabled={
													isEditMode ||
													(isV2UI
														? isStrictLocked(values.vendor_id)
														: hasPrefilledData)
												}
												onChange={(value) => {
													if (
														isV2UI &&
														isStrictLocked(values.vendor_id)
													) {
														return;
													}
													if (onVendorChange) {
														onVendorChange(
															value,
															setFieldValue,
															values.vehicle_type,
															values
														);
													} else {
														setFieldValue('vendor_id', value);
														// Clear vehicle number in V2 UI when vendor changes
														if (onVehicleTypeChange) {
															setFieldValue(
																'vehicle_number',
																undefined
															);
														}
													}
												}}
												filterOption={(input, option) =>
													option.label
														.toLowerCase()
														.indexOf(input.toLowerCase()) >= 0
												}
											/>
										</FormInput>
									</CompactCol>
									<CompactCol span={12}>
										<FormInput name="vendor_rate" label="Vendor Rate *">
											<CustomInput
												name="vendor_rate"
												placeholder="Vendor Rate"
												type="number"
												value={values.vendor_rate}
												onChange={(e) =>
													setFieldValue('vendor_rate', e.target.value)
												}
												onKeyPress={(e) => {
													// Allow only numbers, backspace, delete, arrow keys, etc.
													const allowedKeys = [
														'0',
														'1',
														'2',
														'3',
														'4',
														'5',
														'6',
														'7',
														'8',
														'9',
														'.',
														'Backspace',
														'Delete',
														'ArrowLeft',
														'ArrowRight',
														'Tab',
													];
													if (!allowedKeys.includes(e.key)) {
														e.preventDefault();
													}
												}}
												disabled={
													isEditMode ||
													isStrictLocked(values.vendor_rate) ||
													isvendorBilling ||
													(hasPrefilledData && formData?.vendor_rate)
												}
											/>
										</FormInput>
									</CompactCol>
								</CompactRow>
								{values.trip_type === 'adhoc' && (
									<CompactRow gutter={16}>
										<CompactCol span={24}>
											<FormInput name="remarks" label="Remarks">
												<Input.TextArea
													name="remarks"
													placeholder="Remarks"
													value={values.remarks}
													onChange={(e) =>
														setFieldValue('remarks', e.target.value)
													}
													maxLength={250}
													disabled={
														isEditMode || (isV2UI && isAdhocPrefilled)
													}
												/>
											</FormInput>
										</CompactCol>
									</CompactRow>
								)}
								<CompactRow gutter={16}>
									<CompactCol span={12}>
										<FormInput name="client_rate" label="Client Rate *">
											<CustomInput
												name="client_rate"
												placeholder="Client Rate"
												type="number"
												value={values.client_rate}
												onChange={(e) =>
													setFieldValue('client_rate', e.target.value)
												}
												onKeyPress={(e) => {
													const allowedKeys = [
														'0',
														'1',
														'2',
														'3',
														'4',
														'5',
														'6',
														'7',
														'8',
														'9',
														'.',
														'Backspace',
														'Delete',
														'ArrowLeft',
														'ArrowRight',
														'Tab',
													];
													if (!allowedKeys.includes(e.key)) {
														e.preventDefault();
													}
												}}
												disabled={
													isEditMode ||
													isStrictLocked(values.client_rate) ||
													isvendorBilling ||
													(hasPrefilledData && formData?.client_rate)
												}
											/>
										</FormInput>
									</CompactCol>
									<CompactCol span={12}>
										<FormInput name="vehicle_number" label="Vehicle No *">
											{onVendorChange || onVehicleTypeChange ? (
												<Select
													name="vehicle_number"
													showSearch
													options={vehicleNumbers}
													placeholder="Select Vehicle Number"
													disabled={
														(isEditMode &&
															!canSelectCrossVendorVehicle) ||
														(!isV2UI &&
															hasPrefilledData &&
															formData?.vehicle_number) ||
														!values.vendor_id ||
														!values.vehicle_type
													}
													onChange={(e) => {
														if (onVehicleNumberChange) {
															onVehicleNumberChange(
																e,
																setFieldValue,
																values
															);
														} else {
															setFieldValue('vehicle_number', e);
														}
													}}
													filterOption={(input, option) =>
														option.label
															.toLowerCase()
															.indexOf(input.toLowerCase()) >= 0
													}
													dropdownRender={
														canSelectCrossVendorVehicle
															? // eslint-disable-next-line react/no-unstable-nested-components
															  (menu) => (
																	<>
																		{menu}
																		<div
																			role="button"
																			tabIndex={0}
																			onMouseDown={(ev) => {
																				ev.preventDefault();
																				setCrossVendorVisible(
																					true
																				);
																			}}
																			style={{
																				padding: '8px 12px',
																				color: '#18405D',
																				cursor: 'pointer',
																				fontWeight: 500,
																				textDecoration:
																					'underline',
																				borderTop:
																					'1px solid #f0f0f0',
																			}}
																		>
																			Select from different
																			vendor
																		</div>
																	</>
															  )
															: undefined
													}
												/>
											) : (
												<CustomInput
													name="vehicle_number"
													placeholder="Vehicle Number"
													isCaps
													disabled={
														isEditMode ||
														(hasPrefilledData &&
															formData?.vehicle_number)
													}
												/>
											)}
										</FormInput>
										{canReplaceVehicle && (
											<ReplaceLinkRow>
												<ReplaceLink
													type="button"
													disabled={!isFormEditable}
													onClick={() => {
														if (!isFormEditable) return;
														setReplaceVehicleVisible(true);
													}}
												>
													<RetweetOutlined />
													Replace Vehicle
												</ReplaceLink>
											</ReplaceLinkRow>
										)}
									</CompactCol>
								</CompactRow>
								<CompactRow gutter={16}>
									<CompactCol span={12}>
										<FormInput
											name={isV2UI ? 'driver_id' : 'driver_name'}
											label="Driver Name *"
										>
											{isV2UI ? (
												<Select
													name="driver_id"
													showSearch
													options={
														values.driver_id &&
														!(driverData || []).some(
															(o) => o.value === values.driver_id
														)
															? [
																	...(driverData || []),
																	{
																		value: values.driver_id,
																		label:
																			values.driver_name ||
																			String(
																				values.driver_id
																			),
																	},
															  ]
															: driverData
													}
													placeholder="Select Driver"
													disabled={
														!canAssignDriverForLaterStatus &&
														((isEditMode && !isFormEditable) ||
															!values.vehicle_number ||
															(isEditMode &&
																isFormEditable &&
																!isDriverStatusEditable &&
																!canSelectCrossVendorVehicle))
													}
													onChange={(e) => {
														if (onDriverChange) {
															onDriverChange(e, setFieldValue);
														} else {
															setFieldValue('driver_id', e);
														}
													}}
													filterOption={(input, option) =>
														option.label
															.toLowerCase()
															.indexOf(input.toLowerCase()) >= 0
													}
												/>
											) : (
												<CustomInput
													name="driver_name"
													placeholder="Driver Name"
													disabled={
														(isEditMode && !isFormEditable) ||
														(hasPrefilledData &&
															formData?.driver_name) ||
														(isEditMode &&
															isFormEditable &&
															!isDriverStatusEditable)
													}
												/>
											)}
										</FormInput>
										{isEditMode && isV2UI && hasReplaceDriverPermission && (
											<ReplaceLinkRow>
												<ReplaceLink
													type="button"
													disabled={!isFormEditable || !canReplaceDriver}
													title={
														!canReplaceDriver
															? 'Driver can be replaced only after the trip has started'
															: undefined
													}
													onClick={() => {
														if (!isFormEditable || !canReplaceDriver)
															return;
														setReplaceDriverVisible(true);
													}}
												>
													<RetweetOutlined />
													Replace Driver
												</ReplaceLink>
											</ReplaceLinkRow>
										)}
									</CompactCol>
									<CompactCol span={12}>
										<FormInput name="driver_number" label="Driver Number *">
											{isV2UI ? (
												<CustomInput
													name="driver_number"
													placeholder="Driver Number"
													value={values.driver_number}
													readOnly
													disabled={
														isEditMode ||
														(hasPrefilledData &&
															formData?.driver_number) ||
														!values.driver_id
													}
												/>
											) : (
												<CustomInput
													name="driver_number"
													placeholder="Driver Number"
													maxLength={10}
													value={values.driver_number}
													onChange={(e) => {
														const { value } = e.target;
														const numericValue = value.replace(
															/[^0-9]/g,
															''
														);
														if (numericValue.length > 0) {
															const firstDigit = parseInt(
																numericValue[0],
																10
															);
															if (
																![6, 7, 8, 9].includes(firstDigit)
															) {
																return;
															}
														}
														if (numericValue.length <= 10) {
															setFieldValue(
																'driver_number',
																numericValue
															);
														}
													}}
													onKeyPress={(e) => {
														const isNumber = /[0-9]/.test(e.key);
														const isControlKey = [
															'Backspace',
															'Delete',
															'ArrowLeft',
															'ArrowRight',
															'Tab',
															'Enter',
														].includes(e.key);
														if (isNumber && !e.target.value) {
															const digit = parseInt(e.key, 10);
															if (![6, 7, 8, 9].includes(digit)) {
																e.preventDefault();
																return;
															}
														}

														if (!isNumber && !isControlKey) {
															e.preventDefault();
														}
													}}
													disabled={
														(isEditMode && !isFormEditable) ||
														(hasPrefilledData &&
															formData?.driver_number)
													}
												/>
											)}
										</FormInput>
									</CompactCol>
								</CompactRow>
								<CompactRow gutter={16}>
									<CompactCol span={12}>
										<FormInput name="vehicle_type" label="Vehicle Type *">
											<Select
												name="vehicle_type"
												showSearch
												options={vehicleTypes}
												placeholder="Select"
												disabled={
													isEditMode ||
													(isV2UI
														? isStrictLocked(values.vehicle_type)
														: hasPrefilledData)
												}
												onChange={(value) => {
													if (
														isV2UI &&
														isStrictLocked(values.vehicle_type)
													) {
														return;
													}
													if (onVehicleTypeChange) {
														onVehicleTypeChange(
															value,
															setFieldValue,
															values.vendor_id,
															values
														);
													} else {
														setFieldValue('vehicle_type', value);
														// Clear vehicle number in V2 UI when vehicle type changes
														if (onVendorChange) {
															setFieldValue(
																'vehicle_number',
																undefined
															);
														}
													}
												}}
											/>
										</FormInput>
									</CompactCol>
									<CompactCol span={12}>
										<div style={{ fontWeight: 500, marginBottom: 8 }}>
											Upload Proof of Request *
										</div>
										<UploadWithPreview
											beforeUpload={async (file) =>
												onProofUpload(file, setFieldValue)
											}
											uploading={uploading}
											disabled={isEditMode && !isFormEditable}
											uploadError={uploadError}
											uploadedFile={
												values.requirement_proof &&
												values.requirement_proof.length > 0
													? values.requirement_proof[0]
													: null
											}
											onFileRemove={() => {
												// Prevent deletion if data is pre-filled from adhoc-request
												if (
													hasPrefilledData &&
													formData?.requirement_proof &&
													formData?.requirement_proof.length > 0
												) {
													message.error(
														"Can't remove for approved adhoc request"
													);
													return false; // Return false to prevent success message
												}
												setFieldValue('requirement_proof', []);
												return true; // Return true to allow success message
											}}
											hideRemove={
												isEditMode &&
												formData?.is_approved === true &&
												formData?.por_approval_status !== 'rejected'
											}
											buttonText="Upload Proof"
											accept={['.pdf', '.jpg', '.jpeg', '.png']}
											entity="trip"
										/>
										{touched.requirement_proof && errors.requirement_proof && (
											<div style={{ color: 'red', marginTop: 4 }}>
												{errors.requirement_proof}
											</div>
										)}
									</CompactCol>
								</CompactRow>
								{isEditMode && (
									<CompactRow gutter={16}>
										<CompactCol span={12}>
											<FormInput name="start_km" label="Start KM *">
												<CustomInput
													name="start_km"
													placeholder="Enter Start KM"
													type="number"
													value={values.start_km}
													onChange={(e) =>
														setFieldValue('start_km', e.target.value)
													}
													disabled={!isFormEditable}
												/>
											</FormInput>
										</CompactCol>
									</CompactRow>
								)}
								<FormButtonContainer>
									<FormCancelButton
										type="button"
										onClick={() => navigate('/trips/intercity')}
									>
										Previous
									</FormCancelButton>
									{isEditMode ? (
										<FormPrimaryButton
											type="submit"
											disabled={formData?.isEnded || !isFormEditable}
										>
											Submit
										</FormPrimaryButton>
									) : (
										<FormPrimaryButton
											type="button"
											disabled={
												!isFormValid || isSubmitting || formData?.isEnded
											}
											loading={isSubmitting}
											onClick={async () => {
												// Validate all fields and show red touches for missing fields
												const validationErrors =
													await formRef.current?.validateForm();
												if (
													validationErrors &&
													Object.keys(validationErrors).length > 0
												) {
													// Set all fields as touched to show red borders
													const allTouchedFields = {};
													Object.keys(validationErrors).forEach(
														(field) => {
															allTouchedFields[field] = true;
														}
													);
													formRef.current?.setTouched(allTouchedFields);
													return; // Don't proceed if there are validation errors
												}

												setPendingValues({
													...values,
													helpers: { setSubmitting },
												});
												if (formRef.current?.values) {
													const encoded = encodeURIComponent(
														JSON.stringify(formRef.current.values)
													);
													navigate(`/trips/create-lr?data=${encoded}`, {
														state: { from: location.pathname },
													});
												}
											}}
										>
											Next
										</FormPrimaryButton>
									)}
								</FormButtonContainer>
							</Form>
							{canSelectCrossVendorVehicle && (
								<VehicleSelectionFromAnotherVendorModal
									visible={crossVendorVisible}
									billingVendorId={values.vendor_id}
									onClose={() => setCrossVendorVisible(false)}
									onSelect={(payload) => {
										if (onCrossVendorSelect) {
											onCrossVendorSelect(payload, setFieldValue, values);
										}
										setCrossVendorVisible(false);
									}}
								/>
							)}
							{(canReplaceVehicle || canReplaceDriver) && (
								<>
									<ReplaceVehicleModal
										mode="trip"
										visible={replaceVehicleVisible}
										tripData={{
											trip_id: formData?.id,
											vendor_id: values.vendor_id,
											vendor_name: formData?.vendor_name,
											vehicle_number: values.vehicle_number,
											vehicle_type: values.vehicle_type,
											vehicle_type_id:
												vehicleTypeIdMap?.[values.vehicle_type],
											driver_id: values.driver_id,
											driver_name: values.driver_name,
											current_cost: values.vendor_rate,
											vendor_rate_basis: formData?.vendor_rate_basis,
											billing_type: formData?.billing_type,
											billing_subtype: formData?.billing_subtype,
											current_vendor_rate: formData?.current_vendor_rate,
											current_charges_per_km:
												formData?.current_charges_per_km,
											order_rates: formData?.order_rates,
										}}
										onConfirm={(payload) => {
											console.log('Replace vehicle payload', payload);
											if (payload.new_vehicle_number) {
												setFieldValue(
													'vehicle_number',
													payload.new_vehicle_number
												);
											}
											if (payload.new_driver_id) {
												setFieldValue('driver_id', payload.new_driver_id);
												setFieldValue(
													'driver_name',
													payload.new_driver_name || ''
												);
												setFieldValue(
													'driver_number',
													payload.new_driver_number || ''
												);
											}
											message.success('Vehicle replaced successfully');
											setReplaceVehicleVisible(false);
											setTimeout(() => window.location.reload(), 800);
											return true;
										}}
										onClose={() => setReplaceVehicleVisible(false)}
									/>
									<ReplaceDriverModal
										visible={replaceDriverVisible}
										tripData={{
											trip_id: formData?.id,
											vendor_id: values.vendor_id,
											vendor_name: formData?.vendor_name,
											vehicle_type: values.vehicle_type,
											vehicle_type_id:
												vehicleTypeIdMap?.[values.vehicle_type],
											driver_id: values.driver_id,
											driver_name: values.driver_name,
										}}
										onConfirm={(payload) => {
											console.log('Replace driver payload', payload);
											setFieldValue('driver_id', payload.new_driver_id);
											setFieldValue(
												'driver_name',
												payload.new_driver_name || ''
											);
											setFieldValue(
												'driver_number',
												payload.new_driver_number || ''
											);
											message.success('Driver replaced successfully');
											setReplaceDriverVisible(false);
											setTimeout(() => window.location.reload(), 800);
											return true;
										}}
										onClose={() => setReplaceDriverVisible(false)}
									/>
								</>
							)}
						</>
					);
				}}
			</Formik>
		</FormContainer>
	);
}

IntercityTripForm.propTypes = {
	isEditMode: PropTypes.bool,
	formData: PropTypes.object,
	cities: PropTypes.array,
	projectData: PropTypes.array,
	routeData: PropTypes.array,
	vendorsData: PropTypes.array,
	vehicleTypes: PropTypes.array,
	vehicleTypeIdMap: PropTypes.object,
	vehicleNumbers: PropTypes.array,
	driverData: PropTypes.array,
	isV2UI: PropTypes.bool,
	sourceCity: PropTypes.string,
	destCity: PropTypes.string,
	uploading: PropTypes.bool,
	uploadError: PropTypes.string,
	vendorUserType: PropTypes.array,
	setPendingValues: PropTypes.func,
	formRef: PropTypes.any,
	onCityChange: PropTypes.func,
	onProjectChange: PropTypes.func,
	onRouteChange: PropTypes.func,
	onVendorTypeChange: PropTypes.func,
	onVendorChange: PropTypes.func,
	onVehicleTypeChange: PropTypes.func,
	onVehicleNumberChange: PropTypes.func,
	clientAllowsCrossVendor: PropTypes.bool,
	onCrossVendorSelect: PropTypes.func,
	onDriverChange: PropTypes.func,
	onPincodeChange: PropTypes.func,
	onProofUpload: PropTypes.func,
	onSubmit: PropTypes.func,
	navigate: PropTypes.func,
	isvendorBilling: PropTypes.bool,
};
